import { createRouter, createWebHistory } from "vue-router";
import SessionDetail from "@/components/features/SessionDetail.vue"

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', name: 'home', component: () => import('../views/Home.vue'), meta: { title: 'LeGizmo - Cycling Aerodynamics' } },
    { path: '/error', name: 'error', component: () => import('../views/PageError.vue'), meta: { title: 'LeGizmo - Error' } },
    { path: '/unauthorized', name: 'unauthorized', component: () => import('../views/Authentication/UnauthorizedUser.vue'), meta: { title: 'LeGizmo - Unauthorized' } },
    { path: '/login', name: 'login', component: () => import('../views/Authentication/PageLogin.vue'), meta: { title: 'LeGizmo - Login' } },
    { path: '/register', name: 'register', component: () => import('../views/Authentication/PageRegister.vue'), meta: { title: 'LeGizmo - Register' } },
    { path: '/seminars', name: 'seminars', component: () => import('../views/Seminars.vue'), meta: { title: 'LeGizmo - Aero Seminars' } },
    { path: '/support', name: 'support', component: () => import('../views/Support.vue'), meta: { title: 'LeGizmo - Support' } },
    { path: '/technology', name: 'technology', component: () => import('../views/Technology.vue'), meta: { title: 'LeGizmo - Technology' } },
    // requiresAuth: true is deliberate here, unlike this route's public-page
    // siblings above -- the standalone/no-login Virtual Elevation tool is
    // hidden for now (see Header.vue's Tools nav link, currently removed).
    { path: '/tools/virtual-elevation', name: 'tools-virtual-elevation', component: () => import('../views/Tools/VirtualElevationFitTool.vue'), meta: { title: 'LeGizmo - Virtual Elevation Tool', requiresAuth: true } },
    { path: '/about', name: 'about', component: () => import('../views/About.vue'), meta: { title: 'LeGizmo - About Us' } },
    { path: '/shared-reports', name: 'shared-reports', component: () => import('../views/Reports/SharedReports.vue'), meta: { title: 'LeGizmo - Shared Reports', requiresAuth: true } },
    { path: '/find-coach', name: 'find-coach', component: () => import('../views/FindCoach.vue'), meta: { title: 'LeGizmo - Find a Coach' } },
    { path: '/report/:id', name: 'report-detail', component: () => import('../views/Reports/ReportDetail.vue'), meta: { title: 'LeGizmo - Report Detail', requiresAuth: true } },
    { path: '/shared-session/:id', name: 'shared-session-detail', component: () => import('../views/Reports/SharedSessionDetail.vue'), meta: { title: 'LeGizmo - Session Detail', requiresAuth: true } },
    { path: '/shared-session/:id/analysis', name: 'shared-session-analysis', component: () => import('../views/Reports/SharedSessionAnalysis.vue'), meta: { title: 'LeGizmo - Session Analysis', requiresAuth: true } },
    { path: '/privacy-policy', name: 'privacy-policy', component: () => import('../views/Legal/PrivacyPolicy.vue'), meta: { title: 'LeGizmo - Privacy Policy' } },
    { path: '/manage-events', name: 'manage-events', component: () => import('../views/Events/ManageEvents.vue'), meta: { title: 'LeGizmo - Manage Events', requiresAuth: true } },
    { path: '/garmin-callback', name: 'garmin-callback', component: () => import('../views/Authentication/GarminCallback.vue'), meta: { title: 'LeGizmo - Garmin Connection', requiresAuth: true } },
    { path: '/oauth/consent', name: 'oauth-consent', component: () => import('../views/Authentication/OAuthConsent.vue'), meta: { title: 'LeGizmo - Authorize AI Agent' } },

    {
      path: '/video-analysis',
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'video-analysis-landing', component: () => import('../views/Video Analysis/AnalysisLanding.vue'), meta: { title: 'LeGizmo - Video Analysis' } },
        { path: 'dashboard', name: 'video-analysis-dashboard', component: () => import('../views/Video Analysis/Dashboard.vue'), meta: { title: 'LeGizmo - Analysis Dashboard' } },
        { path: 'analyzer', name: 'video-analyzer', component: () => import('../views/Video Analysis/VideoAnalyzer.vue'), meta: { title: 'LeGizmo - Video Analyzer' } }
      ]
    },
    {
      path: '/sessions/:id',
      name: 'session-detail',
      component: SessionDetail,
      meta: { title: 'LeGizmo - Session', requiresAuth: true }
    },
    {
      path: '/sessions/:id/cfd',
      name: 'session-cfd',
      component: () => import('../views/Reports/SessionCfdView.vue'),
      meta: { title: 'LeGizmo - Position Explorer', requiresAuth: true }
    },
    {
      path: '/sessions/:id/virtual-elevation',
      name: 'session-virtual-elevation',
      component: () => import('../views/Reports/VirtualElevationView.vue'),
      meta: { title: 'LeGizmo - Virtual Elevation', requiresAuth: true }
    },
    {
      path: '/sessions/:id/analytics',
      name: 'session-analytics',
      component: () => import('../views/Reports/SessionAnalyticsView.vue'),
      meta: { title: 'LeGizmo - AI Analytics', requiresAuth: true }
    },
    {
      path: '/sessions/:id/post-ride-processing',
      name: 'session-post-ride-processing',
      component: () => import('../views/Reports/SessionDeepAnalysisView.vue'),
      meta: { title: 'LeGizmo - Post-Ride Processing', requiresAuth: true }
    },
    {
      path: '/sessions/:id/share',
      name: 'session-share',
      component: () => import('../views/Reports/SessionShareView.vue'),
      meta: { title: 'LeGizmo - Share Session', requiresAuth: true }
    },
    {
      path: '/sessions/:id/race-predictor',
      name: 'session-race-predictor',
      component: () => import('../views/Reports/RacePredictorView.vue'),
      meta: { title: 'LeGizmo - Race Predictor', requiresAuth: true }
    },
    {
      path: '/admin/sessions',
      name: 'admin-sessions',
      component: () => import('../views/Admin/AllSessions.vue'),
      meta: { title: 'LeGizmo - All Sessions', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/courses',
      name: 'admin-courses',
      component: () => import('../views/Admin/CourseLibrary.vue'),
      meta: { title: 'LeGizmo - Course Library', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/waiting-list',
      name: 'admin-waiting-list',
      component: () => import('../views/Admin/WaitingList.vue'),
      meta: { title: 'LeGizmo - Waiting List', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/org',
      name: 'admin-org',
      component: () => import('../views/Admin/OrgHierarchy.vue'),
      meta: { title: 'LeGizmo - Org Hierarchy', requiresAuth: true, requiresAdmin: true }
    },
    // Architecture reference. Admin-only: it describes the whole system,
    // including where data lives and what the trust boundaries are.
    {
      path: '/architecture',
      name: 'architecture-platform',
      component: () => import('../views/Architecture/PlatformFlowView.vue'),
      meta: { title: 'LeGizmo - Architecture', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/architecture/technology',
      name: 'architecture-technology',
      component: () => import('../views/Architecture/TechnologyStackView.vue'),
      meta: { title: 'LeGizmo - Technology & Structure', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('../views/Admin/UserManagement.vue'),
      meta: { title: 'LeGizmo - User Management', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/devices',
      name: 'admin-devices',
      component: () => import('../views/Admin/AeroSensorInventory.vue'),
      meta: { title: 'LeGizmo - Aero Sensor Inventory', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/firmware',
      name: 'admin-firmware',
      component: () => import('../views/Admin/FirmwareManagement.vue'),
      meta: { title: 'LeGizmo - Firmware Management', requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/teams/analytics/:entityId?',
      name: 'team-analytics',
      component: () => import('../views/Reports/TeamAnalytics.vue'),
      meta: { title: 'LeGizmo - Team Analytics', requiresAuth: true }
    },
    {
      path: '/analytics/:id?',
      name: 'athlete-analytics',
      component: () => import('../views/Team/AthleteAnalyticsView.vue'),
      meta: { title: 'LeGizmo - Training Analytics', requiresAuth: true }
    },
    {
      path: '/my-team',
      name: 'my-team',
      component: () => import('../views/Team/MyTeamHierarchyView.vue'),
      meta: { title: 'LeGizmo - My Team', requiresAuth: true }
    },
    {
      path: '/my-team/athlete/:id',
      name: 'athlete-profile',
      component: () => import('../views/Team/AthleteProfileView.vue'),
      meta: { title: 'LeGizmo - Athlete Profile', requiresAuth: true }
    },
    {
      path: '/teams/:entityId/collections',
      name: 'team-collections',
      component: () => import('../views/Team/CollectionsListView.vue'),
      meta: { title: 'LeGizmo - Collections', requiresAuth: true }
    },
    {
      path: '/collections/:collectionId',
      name: 'collection-detail',
      component: () => import('../views/Team/CollectionDetailView.vue'),
      meta: { title: 'LeGizmo - Collection', requiresAuth: true }
    },
    {
      path: '/my-profile',
      name: 'my-profile',
      component: () => import('../views/Team/AthleteProfileView.vue'),
      meta: { title: 'LeGizmo - My Profile', requiresAuth: true }
    },
    {
      path: '/coaching',
      name: 'coaching',
      component: () => import('../views/Coaching/CoachingRelationships.vue'),
      meta: { title: 'LeGizmo - Coaching', requiresAuth: true }
    },
    { path: '/integrations', name: 'partner-integrations', component: () => import('../views/Integrations/PartnerIntegrations.vue'), meta: { title: 'LeGizmo - Partner Integrations', requiresAuth: true } },
    { path: '/strava-callback', name: 'strava-callback', component: () => import('../views/Authentication/StravaCallback.vue'), meta: { title: 'LeGizmo - Strava Connection', requiresAuth: true } },
    { path: '/:pathMatch(.*)*', component: () => import('../views/PageError.vue'), meta: { title: 'LeGizmo - Page Not Found' } }

  ],
});

import { useAuthStore } from "@/stores/storeAuth";
import { useFirebaseAppStore } from "@/stores/storeFirebaseApp";
import { resolvePostLoginRoute } from "@/services/authRedirect";

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * firebaseAppStore.isManager (managedEntities.length > 0) is only known once
 * storeFirebaseApp's init() has finished its own async fetches -- isLoading
 * flips false at the end of that action. Deciding the role-based redirect
 * below before that finishes reads managedEntities while it's still its
 * initial empty [], misclassifying a real manager as a plain user (the
 * "sometimes lands on the wrong dashboard on login" bug). Poll briefly
 * rather than reading state immediately; this only affects the one guard
 * pass that redirects away from /login.
 */
async function waitForFirebaseAppReady(firebaseAppStore: ReturnType<typeof useFirebaseAppStore>): Promise<void> {
  for (let i = 0; i < 25; i++) {
    if (!firebaseAppStore.isLoading) return;
    await delay(100);
  }
}

router.beforeEach(async (to, from, next) => {
  const title = to.meta.title as string
  if (title) {
    document.title = title
  }

  const authStore = useAuthStore();
  const firebaseAppStore = useFirebaseAppStore();

  // Guard: if route requires authentication and user is not logged in, redirect to login
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  if (requiresAuth && !authStore.user.uid) {
    next('/login');
    return;
  }

  // Guard: admin-only routes. This is UX only — firestore.rules is what
  // actually prevents a non-admin from reading other users' sessions.
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  if (requiresAdmin && firebaseAppStore.currentUser?.role?.toLowerCase() !== 'admin') {
    next('/unauthorized');
    return;
  }

  // Guard: if user is logged in, redirect them away from auth pages to their results/admin/manager dashboard
  if (authStore.user.uid) {
    if (to.path === '/login') {
      await waitForFirebaseAppReady(firebaseAppStore);
      next(resolvePostLoginRoute(firebaseAppStore));
      return;
    }
  }

  next();
})

export default router;
